'use client';

import { useSyncHeight } from '@/app/hooks';
import { useHeroToolForm } from '../../hooks/use-hero-tool-form';
import type { HeroSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { StickyToolInputBar } from '../StickyToolInputBar';
import { HeroInputForm } from './ImmersiveHero/components/HeroInputForm';
import { PresetPromptChips } from './ImmersiveHero/components/PresetPromptChips';

export function SplitLayoutHero({ section }: { section: HeroSectionData }) {
  const toolType = section.toolType ?? null;
  const buttonHref = section.button?.href;

  const {
    textPrompt,
    setTextPrompt,
    referenceImages,
    handlePresetClick,
    handleCreate,
    handleImageUpload,
    handleRemoveImage,
  } = useHeroToolForm(toolType, buttonHref);

  useSyncHeight({ selector: '[data-split-hero-sync]' });

  return (
    <>
      <section className="relative flex w-full items-center justify-center overflow-hidden bg-[#0a0a0f] pt-[164px] pb-20 md:pt-16 md:pb-12">
        <div className="mx-auto flex w-full max-w-none items-start justify-center gap-[92px] px-10 sm:px-0 md:px-4 lg:max-w-[1248px] lg:flex-col lg:items-start lg:justify-start lg:gap-8">
          <div
            className="lg:!h-auto flex w-[516px] flex-shrink-0 flex-col justify-between gap-6 lg:w-full"
            data-split-hero-sync
          >
            <div className="flex flex-col gap-4">
              <h1 className="font-semibold text-[48px] text-white leading-[1.2] sm:text-[36px]">{section.title}</h1>
              {section.description && (
                <PortableTextContent className="text-base leading-[1.6] [&_p]:text-white" value={section.description} />
              )}
            </div>

            {toolType && (
              <div className="flex flex-col gap-3">
                <HeroInputForm
                  textPrompt={textPrompt}
                  referenceImages={referenceImages}
                  toolType={toolType}
                  onChangePrompt={setTextPrompt}
                  onCreate={handleCreate}
                  onImageUpload={handleImageUpload}
                  onRemoveImage={handleRemoveImage}
                />

                {!!section.presetPrompts?.length && (
                  <div className="flex items-center gap-[14px]">
                    <div className="font-medium text-[17px] text-white">Try Samples</div>
                    <PresetPromptChips
                      onClickPreset={handlePresetClick}
                      presets={section.presetPrompts}
                      variant="square"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className="lg:!h-auto relative aspect-[4/3] min-w-0 flex-none overflow-hidden rounded-16 lg:w-full"
            data-split-hero-sync
          >
            <MediaAsset
              className="absolute inset-0 h-full w-full object-cover lg:relative lg:h-auto"
              media={section.showcaseMedia}
            />
          </div>
        </div>
      </section>

      {toolType && (
        <StickyToolInputBar
          toolType={toolType}
          textPrompt={textPrompt}
          referenceImages={referenceImages}
          onChangePrompt={setTextPrompt}
          onCreate={handleCreate}
          onImageUpload={handleImageUpload}
          onRemoveImage={handleRemoveImage}
        />
      )}
    </>
  );
}
